import React from 'react'
import { Theme } from '../types'
import { themeToStyleObject } from '../utils/themeToCSS'

interface LandingPagePreviewProps {
  theme: Theme
  isDarkMode: boolean
}

export function LandingPagePreview({ theme, isDarkMode }: LandingPagePreviewProps) {
  const style = themeToStyleObject(theme, isDarkMode)
  const colors = isDarkMode ? theme.darkMode : theme.lightMode

  return (
    <div
      className="min-h-full overflow-auto"
      style={{
        ...style,
        backgroundColor: 'var(--theme-background)',
        color: 'var(--theme-text)',
        fontFamily: 'var(--theme-body-font)',
        fontWeight: 'var(--theme-body-weight)' as any,
      }}
    >
      {/* Navigation */}
      <nav
        className="sticky top-0 z-10 border-b"
        style={{
          backgroundColor: 'var(--theme-surface)',
          borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div
            className="text-xl"
            style={{
              fontFamily: 'var(--theme-display-font)',
              fontWeight: 'var(--theme-display-weight)' as any,
              color: 'var(--theme-primary)',
            }}
          >
            BrandName
          </div>
          <div className="flex items-center gap-6">
            <a href="#" style={{ color: 'var(--theme-text-muted)' }} className="text-sm hover:opacity-80">Features</a>
            <a href="#" style={{ color: 'var(--theme-text-muted)' }} className="text-sm hover:opacity-80">Pricing</a>
            <a href="#" style={{ color: 'var(--theme-text-muted)' }} className="text-sm hover:opacity-80">About</a>
            <button
              className="px-4 py-2 text-sm text-white"
              style={{
                backgroundColor: 'var(--theme-primary)',
                borderRadius: 'var(--theme-border-radius)',
              }}
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1
            className="text-5xl md:text-7xl mb-6"
            style={{
              fontFamily: 'var(--theme-display-font)',
              fontWeight: 'var(--theme-display-weight)' as any,
              color: 'var(--theme-text)',
              lineHeight: 1.1,
            }}
          >
            Build Something{' '}
            <span style={{ color: 'var(--theme-primary)' }}>Extraordinary</span>
          </h1>
          <p
            className="text-xl mb-10 max-w-2xl mx-auto"
            style={{ color: 'var(--theme-text-muted)' }}
          >
            The modern platform for creative professionals. Design, build, and launch
            beautiful experiences that your users will love.
          </p>
          <div className="flex items-center justify-center gap-4">
            <button
              className="px-8 py-4 text-lg text-white"
              style={{
                backgroundColor: 'var(--theme-primary)',
                borderRadius: 'var(--theme-border-radius)',
                boxShadow: 'var(--theme-shadow)',
              }}
            >
              Start Free Trial
            </button>
            <button
              className="px-8 py-4 text-lg border-2"
              style={{
                borderColor: 'var(--theme-primary)',
                color: 'var(--theme-primary)',
                borderRadius: 'var(--theme-border-radius)',
              }}
            >
              Watch Demo
            </button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6" style={{ backgroundColor: 'var(--theme-surface)' }}>
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl md:text-4xl text-center mb-16"
            style={{
              fontFamily: 'var(--theme-display-font)',
              fontWeight: 'var(--theme-display-weight)' as any,
            }}
          >
            Why Choose Us
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '🚀', title: 'Lightning Fast', desc: 'Optimized for performance. Your users deserve the best experience.' },
              { icon: '🎨', title: 'Beautiful Design', desc: 'Crafted with attention to detail. Every pixel matters.' },
              { icon: '🔒', title: 'Enterprise Security', desc: 'Bank-grade security to protect your most valuable data.' },
            ].map((feature, i) => (
              <div
                key={i}
                className="p-8"
                style={{
                  backgroundColor: 'var(--theme-background)',
                  borderRadius: 'var(--theme-border-radius)',
                  boxShadow: 'var(--theme-shadow)',
                }}
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3
                  className="text-xl mb-3"
                  style={{
                    fontFamily: 'var(--theme-display-font)',
                    fontWeight: 'var(--theme-display-weight)' as any,
                  }}
                >
                  {feature.title}
                </h3>
                <p style={{ color: 'var(--theme-text-muted)' }}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Testimonials */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl md:text-4xl text-center mb-16"
            style={{
              fontFamily: 'var(--theme-display-font)',
              fontWeight: 'var(--theme-display-weight)' as any,
            }}
          >
            Trusted by Creators
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              { quote: 'This platform transformed how we work. The attention to detail is remarkable.', author: 'Sarah Chen', role: 'CEO, TechCorp' },
              { quote: 'Finally, a tool that understands what designers need. Pure excellence.', author: 'Marcus Webb', role: 'Lead Designer, Studio X' },
            ].map((testimonial, i) => (
              <div
                key={i}
                className="p-8 border"
                style={{
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                  borderRadius: 'var(--theme-border-radius)',
                }}
              >
                <p className="text-lg mb-6 italic" style={{ color: 'var(--theme-text)' }}>
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-full"
                    style={{ backgroundColor: 'var(--theme-secondary)', opacity: 0.6 }}
                  />
                  <div>
                    <div
                      className="font-semibold"
                      style={{ fontFamily: 'var(--theme-display-font)' }}
                    >
                      {testimonial.author}
                    </div>
                    <div className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20 px-6"
        style={{ backgroundColor: 'var(--theme-primary)' }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="text-3xl md:text-4xl mb-6 text-white"
            style={{
              fontFamily: 'var(--theme-display-font)',
              fontWeight: 'var(--theme-display-weight)' as any,
            }}
          >
            Ready to Get Started?
          </h2>
          <p className="text-lg mb-8 opacity-90 text-white">
            Join thousands of creators building amazing experiences.
          </p>
          <button
            className="px-8 py-4 text-lg"
            style={{
              backgroundColor: 'var(--theme-accent)',
              color: isDarkMode ? '#0f0f0f' : '#ffffff',
              borderRadius: 'var(--theme-border-radius)',
              boxShadow: 'var(--theme-shadow)',
            }}
          >
            Start Your Free Trial
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="py-12 px-6 border-t"
        style={{
          backgroundColor: 'var(--theme-surface)',
          borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        }}
      >
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div
            className="text-xl"
            style={{
              fontFamily: 'var(--theme-display-font)',
              fontWeight: 'var(--theme-display-weight)' as any,
              color: 'var(--theme-primary)',
            }}
          >
            BrandName
          </div>
          <div className="flex items-center gap-6 text-sm" style={{ color: 'var(--theme-text-muted)' }}>
            <a href="#" className="hover:opacity-80">Privacy</a>
            <a href="#" className="hover:opacity-80">Terms</a>
            <a href="#" className="hover:opacity-80">Contact</a>
          </div>
          <div className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>
            &copy; 2026 BrandName. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}
